import io from "socket.io-client";

export class CustomDataFeed {
  constructor(dataFeedUrl, wsUrl, symbol) {
    this.dataFeedUrl = dataFeedUrl;
    this.wsUrl = wsUrl;
    this.symbol = symbol;
    this.socket = null; // Socket.IO connection
    this.socketInstances = {};
  }

  // Called when the widget is ready to initialize the data feed
  onReady(callback) {
    setTimeout(
      () =>
        callback({
          supported_resolutions: ["1", "5", "15"],
        }),
      0
    );
  }

  // Resolves symbol details to be displayed on the chart
  async resolveSymbol(
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback
  ) {
    try {
      const response = await fetch(
        `${this.dataFeedUrl}/symbols?symbol=${symbolName}`
      );
      const data = await response.json();
      if (!data || data.error) {
        throw new Error("Symbol not found");
      }

      onSymbolResolvedCallback(data);
    } catch (error) {
      console.error("Error resolving symbol:", error);
      onResolveErrorCallback(error); // rethrow the error to be handled by the caller
    }
  }

  // Fetch historical bars (OHLCV data) for the chart
  async getBars(symbolInfo, resolution, periodParams, onResult, onError) {
    try {
      const { from, to, countBack } = periodParams;
      const url = `${this.dataFeedUrl}/history?symbol=${
        symbolInfo.name
      }&resolution=${resolution}&from=${from}&to=${to}&countback=${
        countBack || ""
      }`;

      const response = await fetch(url);
      const data = await response.json();

      const bars = data.t.map((timestamp, index) => ({
        time: timestamp * 1000, // Convert to milliseconds
        open: data.o[index],
        high: data.h[index],
        low: data.l[index],
        close: data.c[index],
        volume: data.v[index],
      }));

      onResult(bars, { noData: data.s === "no_data" });
    } catch (error) {
      console.error("Error fetching historical data:", error);
      onError(error);
    }
  }

  async fetchLatestBars(symbol, resolution, onRealtimeCallback) {
    try {
      const countBack = 2; // Fetch only the most recent bar
      const url = `${
        this.dataFeedUrl
      }/history?symbol=${symbol}&resolution=${resolution}&from=0&to=${Math.floor(
        Date.now() / 1000
      )}&countback=${countBack}`;

      const response = await fetch(url);
      const data = await response.json();

      // Process the last trade data (which might create or update a bar)
      const bars = data.t.map((timestamp, index) => ({
        time: timestamp * 1000, // Convert to milliseconds
        open: data.o[index],
        high: data.h[index],
        low: data.l[index],
        close: data.c[index],
        volume: data.v[index],
      }));

      if (bars.length > 0) {
        bars.forEach((bar) => {
          onRealtimeCallback(bar);
        });
      }
    } catch (error) {
      console.error("Error fetching latest bars:", error);
    }
  }

  // Subscribe to real-time updates using Socket.IO
  subscribeBars(
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscriberUID,
    onResetCacheNeededCallback
  ) {
    console.log(
      `Subscribing to real-time updates for resolution: ${resolution}`
    );
    // Establish a connection to the Socket.IO server
    const socket = io(this.wsUrl, {
      transports: ["websocket"],
    });

    const TRADE_CREATED_EVENT = "NEW_TRADE";

    socket.on("connect", () => {
      console.log("Connected to Socket.IO server");
      socket.on(TRADE_CREATED_EVENT, (data) => {
        if (data?.address === this.symbol) {
          this.fetchLatestBars(
            symbolInfo.ticker,
            resolution,
            onRealtimeCallback
          );
        }
      });
    });

    socket.on("disconnect", () => {
      console.log(
        "Disconnected from Socket.IO server",
        resolution,
        subscriberUID
      );
    });

    this.socketInstances[subscriberUID] = socket;

    socket.on("connect_error", (error) => {
      console.error(
        "Socket.IO connection error:",
        error,
        resolution,
        subscriberUID
      );
    });
  }

  // Unsubscribe from real-time updates using Socket.IO
  unsubscribeBars(subscriberUID) {
    console.log("Unsubscribing from real-time updates");
    if (this.socketInstances[subscriberUID]) {
      this.socketInstances[subscriberUID].disconnect(); // Disconnect the Socket.IO connection
      delete this.socketInstances[subscriberUID];
    }
  }

  searchSymbols(userInput, exchange, symbolType, onResultReadyCallback) {
    onResultReadyCallback([]); // Return an empty array
  }
}
